/**
 * React Query hooks for project management
 * Provides type-safe data fetching and mutations for projects
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query';

import { projectsKeys } from '@/lib/queryClient';
import { api } from '@/services/api';
import type {
  Project,
  ProjectStats,
  CreateProjectRequest,
  UpdateProjectRequest,
} from '@/types';

// ============================================================================
// QUERY HOOKS
// ============================================================================

/**
 * Fetch all projects for a team
 */
export function useProjects(teamId?: string): UseQueryResult<Project[], Error> {
  return useQuery({
    queryKey: projectsKeys.list(teamId),
    queryFn: async () => {
      if (!teamId) {
        return [];
      }
      const projects = await api.get<Project[]>(`/teams/${teamId}/projects`);
      return projects;
    },
    enabled: !!teamId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Fetch a single project by ID
 */
export function useProject(projectId: string): UseQueryResult<Project, Error> {
  return useQuery({
    queryKey: projectsKeys.detail(projectId),
    queryFn: async () => {
      const project = await api.get<Project>(`/projects/${projectId}`);
      return project;
    },
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Fetch project statistics
 */
export function useProjectStats(
  projectId: string
): UseQueryResult<ProjectStats, Error> {
  return useQuery({
    queryKey: projectsKeys.stats(projectId),
    queryFn: async () => {
      const stats = await api.get<ProjectStats>(`/projects/${projectId}/stats`);
      return stats;
    },
    enabled: !!projectId,
    staleTime: 2 * 60 * 1000, // 2 minutes (more frequent for stats)
  });
}

// ============================================================================
// MUTATION HOOKS
// ============================================================================

/**
 * Create a new project
 */
export function useCreateProject(): UseMutationResult<
  Project,
  Error,
  CreateProjectRequest & { teamId: string }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateProjectRequest & { teamId: string }) => {
      const { teamId, ...projectData } = data;
      const project = await api.post<Project>(`/teams/${teamId}/projects`, projectData);
      return project;
    },
    onSuccess: (newProject, variables) => {
      // Invalidate projects list to refetch
      queryClient.invalidateQueries({
        queryKey: projectsKeys.list(variables.teamId),
      });

      // Optimistically add the new project to the cache
      queryClient.setQueryData<Project[]>(
        projectsKeys.list(variables.teamId),
        (old) => {
          if (!old) return [newProject];
          return [...old, newProject];
        }
      );

      // Set the new project detail in cache
      queryClient.setQueryData(
        projectsKeys.detail(newProject.projectId),
        newProject
      );
    },
    onError: (error) => {
      console.error('Failed to create project:', error);
    },
  });
}

/**
 * Update an existing project
 */
export function useUpdateProject(
  projectId: string
): UseMutationResult<Project, Error, UpdateProjectRequest> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateProjectRequest) => {
      const project = await api.put<Project>(`/projects/${projectId}`, data);
      return project;
    },
    onSuccess: (updatedProject) => {
      // Update the project in the detail cache
      queryClient.setQueryData(
        projectsKeys.detail(projectId),
        updatedProject
      );

      // Update the project in the list cache
      queryClient.setQueryData<Project[]>(
        projectsKeys.list(updatedProject.teamId),
        (old) => {
          if (!old) return [updatedProject];
          return old.map((project) =>
            project.projectId === projectId ? updatedProject : project
          );
        }
      );

      // Invalidate to ensure consistency
      queryClient.invalidateQueries({
        queryKey: projectsKeys.lists(),
      });
    },
    onError: (error) => {
      console.error('Failed to update project:', error);
    },
  });
}

/**
 * Delete a project (soft delete)
 */
export function useDeleteProject(
  projectId: string
): UseMutationResult<void, Error, void> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await api.delete(`/projects/${projectId}`);
    },
    onMutate: async () => {
      // Get the project to know which team list to update
      const project = queryClient.getQueryData<Project>(
        projectsKeys.detail(projectId)
      );
      return { teamId: project?.teamId };
    },
    onSuccess: (_, __, context) => {
      if (context?.teamId) {
        // Remove from active list cache
        queryClient.setQueryData<Project[]>(
          projectsKeys.list(context.teamId),
          (old) => {
            if (!old) return [];
            return old.filter((project) => project.projectId !== projectId);
          }
        );

        // Invalidate deleted projects cache
        queryClient.invalidateQueries({
          queryKey: projectsKeys.deleted(context.teamId),
        });
      }

      // Remove detail cache
      queryClient.removeQueries({ queryKey: projectsKeys.detail(projectId) });

      // Remove stats cache
      queryClient.removeQueries({ queryKey: projectsKeys.stats(projectId) });

      // Invalidate to ensure consistency
      queryClient.invalidateQueries({ queryKey: projectsKeys.lists() });
    },
    onError: (error) => {
      console.error('Failed to delete project:', error);
    },
  });
}

/**
 * Deleted project with days remaining info
 */
export interface DeletedProject extends Project {
  deletedAt: string;
  daysRemaining: number;
}

/**
 * Fetch deleted projects for a team
 */
export function useDeletedProjects(
  teamId?: string
): UseQueryResult<DeletedProject[], Error> {
  return useQuery({
    queryKey: projectsKeys.deleted(teamId),
    queryFn: async () => {
      if (!teamId) {
        return [];
      }
      const projects = await api.get<DeletedProject[]>(
        `/teams/${teamId}/deleted-projects`
      );
      return projects;
    },
    enabled: !!teamId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Restore a soft-deleted project
 */
export function useRestoreProject(): UseMutationResult<
  Project,
  Error,
  { projectId: string; teamId: string }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ projectId }) => {
      const project = await api.put<Project>(`/projects/${projectId}/restore`);
      return project;
    },
    onSuccess: (restoredProject, { teamId }) => {
      // Add to active list cache
      queryClient.setQueryData<Project[]>(
        projectsKeys.list(teamId),
        (old) => {
          if (!old) return [restoredProject];
          return [...old, restoredProject];
        }
      );

      // Remove from deleted list cache
      queryClient.setQueryData<DeletedProject[]>(
        projectsKeys.deleted(teamId),
        (old) => {
          if (!old) return [];
          return old.filter((p) => p.projectId !== restoredProject.projectId);
        }
      );

      // Set detail cache
      queryClient.setQueryData(
        projectsKeys.detail(restoredProject.projectId),
        restoredProject
      );

      // Invalidate to ensure consistency
      queryClient.invalidateQueries({ queryKey: projectsKeys.lists() });
    },
    onError: (error) => {
      console.error('Failed to restore project:', error);
    },
  });
}

// ============================================================================
// HELPER HOOKS
// ============================================================================

/**
 * Get recent projects for a team (sorted by updatedAt)
 */
export function useRecentProjects(
  teamId?: string,
  limit: number = 4
): UseQueryResult<Project[], Error> {
  const projectsQuery = useProjects(teamId);

  return {
    ...projectsQuery,
    data: projectsQuery.data
      ?.sort(
        (a, b) =>
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
      )
      .slice(0, limit),
  } as UseQueryResult<Project[], Error>;
}